import React, {useState, useEffect, useRef, useMemo} from 'react';
import { Button as AntButton, Radio, Spin, Tag, Tooltip } from "antd";
import {
    DndContext,
    closestCenter,
    DragOverlay,
    PointerSensor,
    KeyboardSensor,
    useSensor,
    useSensors,
    useDroppable,
} from '@dnd-kit/core';
import {
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
    arrayMove,
    sortableKeyboardCoordinates
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ArrowBackRounded, ArrowForwardRounded, DragHandleRounded, PreviewRounded } from "@mui/icons-material";
import { formatQuestionAnswerTime, objectHasEntry, swapItemsInArray } from "@common/utilityFunctions";
import MathJaxHTMLContent from "@common/MathJaxHTMLContent";
import GroupInstructions from "./GroupInstructions";
import GenericDialog from "@common/GenericDialog";
import { useIndexRoving } from "@hooks/useIndexRoving";

const findContainer = (id, data) => {
    const idString = String(id);
    if (data.columnOrder.includes(idString)) {
        return idString;
    }
    return Object.keys(data.columns).find(key =>
        data.columns[key].questionIds.includes(idString)
    );
};

const ReorderTestQuestionCard = ({ initialIndex, index, test, maxPageSize, handlePreview, dragListeners, dragAttributes, isDragging, ...item }) => {
    const [maxMarkState, setMaxMarkState] = useState(item.maxMark);
    return (
    <article className={'flexColumn smallGap'} style={{ overflow:'hidden', padding: '10px', backgroundColor: '#fff', color: '#333', border: '1px solid #ccc' }}>
        <div className={'flexRow mediumGap'}>
            <div className={'flexAlignCentre'}><p><strong className={'ch-lrg-font'}>{index+1}.</strong></p></div>
            <div className={'flexColumn smallGap flex1'} style={{overflow:'hidden'}}>
                <div>
                    {item.formattedInstructions ? (<MathJaxHTMLContent html={item.formattedInstructions} disableInputs={true} className={'ch-test-editor-card-question-content'}/>)
                        :(item.questionType !== 'GROUP_QUESTION' ? 'Formatted instructions missing' : null)}
                    {item.questionType === 'GROUP_QUESTION' && <GroupInstructions showLoader={false} getMaxMark={(val)=>{setMaxMarkState(val)}} questionData={{...item}} className={'ch-test-editor-card-question-content'} />}
                </div>
                <div className={'flexRow flexWrap smallGap'}>
                    <Tag color={'blue'}>{item.questionAnswerTypeGroupDisplay}</Tag>
                    <Tag color={'purple'}>{maxMarkState} Mark</Tag>
                    {item.answerTime && <Tag color={'gold'}>{formatQuestionAnswerTime(item.answerTime)}</Tag>}
                    <Tag color={'volcano'}>Level {item.testDiffLevel}</Tag>
                </div>
            </div>
            <aside className={'flexColumn flexSpaceAround'}>
                <div>
                    <button
                        className="ch-reorder-btn drag-handle-btn"
                        tabIndex={-1}
                        aria-pressed={isDragging}
                        style={{cursor:'grab', background:'none', border:'none'}}
                        {...dragAttributes}
                        {...dragListeners}
                        aria-label={`Drag question ${initialIndex+1}`}
                    >
                        <DragHandleRounded/>
                    </button>
                </div>
                <button
                    className="ch-reorder-btn preview-btn"
                    tabIndex={-1}
                    aria-label={`Preview question ${initialIndex+1}`}
                    style={{background:'none', border:'none', cursor:'pointer'}}
                    onClick={()=>{handlePreview({test, maxMark: maxMarkState, ...item})}}
                >
                    <PreviewRounded/>
                </button>
            </aside>
        </div>
    </article>
);
};

const SortableItem = ({ item, test, handlePreview }) => {
    const itemIdString = String(item.id);
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: itemIdString });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        userSelect: 'none',
        padding: '2px',
        backgroundColor: isDragging ? '#263b4a' : '#456C86',
        color: 'white',
        borderRadius: '2px',
        transformOrigin: '0 0',
        zIndex: isDragging ? 10 : 'auto',
        opacity: isDragging ? 0.8 : 1,
        boxShadow: isDragging ? '0 4px 8px rgba(0, 0, 0, 0.2)' : 'none',
    };

    const isUnsaved = item.originalSeq !== undefined && item.originalSeq !== item.liveIndex;

    return (
        <div
            className={`ch-column-dnd-card ${isUnsaved ? 'unsaved-order' : ''}`}
            ref={setNodeRef}
            style={style}
        >
            <ReorderTestQuestionCard
                initialIndex={item.questionSeq}
                index={item.liveIndex}
                originalSeq={item.originalSeq}
                key={item.questionId}
                test={test}
                handlePreview={handlePreview}
                dragListeners={listeners}
                dragAttributes={attributes}
                isDragging={isDragging}
                {...item}
            />
        </div>
    );
};

const Column = ({ column, questions, test, id, handleMoveColumn, index, columnTotal, handlePreview, setPendingFocus }) => {
    const { setNodeRef, isOver } = useDroppable({ id: id });
    const columnItemIds = column.questionIds.map(String);

    return (
    <section className={'ch-dnd-column'} aria-labelledby={`col-header-${id}`}>
        <header className={'ch-dnd-column-title flexSpaceBetween'}>
            {index !== 0 ?
                <Tooltip title={`Swap with the page to the left`}>
                    <AntButton aria-label={`Swap with the page to the left`} id={`swap-left-${id}`} className="swap-page-btn" tabIndex={-1} shape={'circle'}
                               onClick={()=>{
                                   if (index-1 === 0) {
                                       setPendingFocus(`swap-right-${id}`);
                                   } else {
                                       setPendingFocus(`swap-left-${id}`);
                                   }
                                   handleMoveColumn((index-1), index);
                               }}
                               type={'text'}>
                        <ArrowBackRounded aria-hidden={true}/>
                    </AntButton>
                </Tooltip>
                : <div></div>}

            <h4 id={`col-header-${id}`}>{column.title} ({column.questionIds.length})</h4>

            {(index !== (columnTotal-1)) ?
                <Tooltip title={`Swap with the page to the right`}>
                    <AntButton aria-label={`Swap with the page to the right`} id={`swap-right-${id}`} className="swap-page-btn" tabIndex={-1} shape={'circle'}
                               onClick={()=>{
                                   if (index + 1 === columnTotal - 1) {
                                       setPendingFocus(`swap-left-${id}`);
                                   } else {
                                       setPendingFocus(`swap-right-${id}`);
                                   }
                                   handleMoveColumn(index, (index+1));
                               }}
                               type={'text'}>
                        <ArrowForwardRounded aria-hidden={true}/>
                    </AntButton>
                </Tooltip>
                : <div></div>}
        </header>
        <div>
            <div
                ref={setNodeRef}
                className={'ch-dnd-column-content'}
                style={{
                    padding: '8px',
                    minHeight: '150px',
                    backgroundColor: isOver ? '#e0f7fa' : '#f9f9f9',
                    flexGrow: 1,
                }}
            >
                <SortableContext
                    id={id}
                    items={columnItemIds}
                    strategy={verticalListSortingStrategy}
                >
                    {questions.map((question) => (
                        <SortableItem key={question.id} item={question} test={test} handlePreview={handlePreview} />
                    ))}

                    {questions.length === 0 && (
                        <div style={{ padding: '10px', color: '#aaa' }}>Drag item here</div>
                    )}
                </SortableContext>
            </div>
        </div>
    </section>
);
};

const customKeyboardCoordinates = (event, args) => {
    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', ' '].includes(event.key)) {
        event.preventDefault();
    }
    return sortableKeyboardCoordinates(event, args);
};

const ReorderView = React.forwardRef(({ useSections = false, initialData, getData, reordering, confirmedReorder, test, maxPageSize = 10 }, ref) => {
    // ... [Keep existing state and hooks exactly the same]
    const [data, setData] = useState(useSections ? {} : initialData);
    const [activeId, setActiveId] = useState(null);
    const [sections, setSections] = useState((useSections && initialData.hasOwnProperty('sections')) ? initialData.sections : {});
    const [sectionVal, setSectionVal] = useState(null);
    const [isQuestionPreviewOpen, setQuestionPreviewOpen] = useState(false);
    const [isLoading, setLoading] = useState(true);
    const [previewQuestionData, setPreviewQuestionData] = useState({});

    const gridRoving = useIndexRoving('.ch-reorder-btn, .swap-page-btn');

    const handlePreview = (questionData) => {
        setQuestionPreviewOpen(true);
        setPreviewQuestionData(questionData);
    };

    const activeContainerRef = useRef(null);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: (maxPageSize+1) } }),
        useSensor(KeyboardSensor, { coordinateGetter: customKeyboardCoordinates })
    );

    const onSectionChange = (e) => {
        const newSectionId = e.target.value;
        const oldSectionId = sectionVal;
        if (oldSectionId && sections[oldSectionId]) {
            setSections(prevSections => ({
                ...prevSections,
                [oldSectionId]: data
            }));
        }
        setSectionVal(newSectionId);
    };

    React.useImperativeHandle(ref, () => ({
        getFinalData: () => {
            let finalData;
            if (useSections) {
                finalData = {
                    ...sections,
                    [sectionVal]: data
                };
            } else {
                finalData = data;
            }
            return finalData;
        }
    }));

    useEffect(() => {
        if(useSections){
            if(sectionVal === null){
                setSectionVal(Object.keys(initialData.sections)[0]);
            }else{
                setData({...sections[sectionVal]});
            }
        }
    }, [sectionVal]);

    useEffect(() => {
        if (activeId !== null) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
        }
    }, [activeId]);

    const fillUpStartColumns = (updateData) => {
        updateData.columnOrder.forEach((colId, idx) => {
            const column = data.columns[colId];
            let count = 0;
            while(column.questionIds.length > maxPageSize || count === 9){
                const nextColumn = data.columns[updateData.columnOrder[(idx+1)]],
                    questionToMove = column.questionIds.pop();
                nextColumn.questionIds.unshift(questionToMove);
                count++;
            }
            count = 0;
            while((column.questionIds.length < maxPageSize && idx < updateData.columnOrder.length-1) || count === 9){
                const nextColumn = data.columns[updateData.columnOrder[(idx+1)]],
                    questionToMove = nextColumn.questionIds.shift();
                column.questionIds.push(questionToMove);
            }
        });
        setData({...updateData});
    };

    const handleDragStart = (event) => {
        setActiveId(event.active.id);
        activeContainerRef.current = findContainer(event.active.id, data);
    };

    const handleDragOver = (event) => {
        const { active, over } = event;
        const activeId = active.id;
        const overId = over?.id;

        if (!overId || activeId === overId) return;

        const activeContainerId = findContainer(activeId, data);
        const overContainerId = findContainer(overId, data);

        if (!activeContainerId || !overContainerId) return;

        if (activeContainerId !== overContainerId) {
            setData((prevData) => {
                const activeItems = prevData.columns[activeContainerId].questionIds;
                const overItems = prevData.columns[overContainerId].questionIds;

                const activeIndex = activeItems.indexOf(activeId);

                let newIndex;
                if (overItems.includes(overId)) {
                    newIndex = overItems.indexOf(overId);
                } else {
                    newIndex = overItems.length;
                }
                if (activeIndex === -1 && overItems.includes(activeId)) return prevData;
                return {
                    ...prevData,
                    columns: {
                        ...prevData.columns,
                        [activeContainerId]: {
                            ...prevData.columns[activeContainerId],
                            questionIds: activeItems.filter((item) => item !== activeId),
                        },
                        [overContainerId]: {
                            ...prevData.columns[overContainerId],
                            questionIds: [
                                ...overItems.slice(0, newIndex),
                                activeId,
                                ...overItems.slice(newIndex, overItems.length),
                            ],
                        },
                    },
                };
            });
        }
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        setActiveId(null);
        if (!over) {
            activeContainerRef.current = null;
            return;
        }
        const sourceContainerId = activeContainerRef.current;
        const destinationContainerId = findContainer(active.id, data);

        activeContainerRef.current = null;

        if (!sourceContainerId || !destinationContainerId) {
            return;
        }
        if (sourceContainerId !== destinationContainerId) {
            if (fillUpStartColumns) {
                fillUpStartColumns(data);
            }
            return;
        }

        const startColumn = data.columns[sourceContainerId];
        const oldIndex = startColumn.questionIds.indexOf(active.id);
        const newIndex = startColumn.questionIds.indexOf(over.id);

        if (oldIndex !== newIndex) {
            const newQuestionIds = arrayMove(startColumn.questionIds, oldIndex, newIndex);
            const newColumn = { ...startColumn, questionIds: newQuestionIds };
            const newState = {
                ...data,
                columns: { ...data.columns, [sourceContainerId]: newColumn },
            };
            setData(newState);
        }
    };

    const handleDragCancel = () => {
        setActiveId(null);
        activeContainerRef.current = null;
    };

    const activeItem = activeId ? data.questions[activeId] : null;

    const handleMoveColumn = (indexA, indexB)=>{
        const updateData = {...data};
        updateData.columnOrder = swapItemsInArray(updateData.columnOrder,indexA,indexB);
        setData(updateData);
        fillUpStartColumns(updateData);
    };

    useEffect(() => {
        setLoading(false);
    }, [isLoading]);

    const pendingFocusRef = useRef(null);

    useEffect(() => {
        if (pendingFocusRef.current) {
            requestAnimationFrame(() => {
                const el = document.getElementById(pendingFocusRef.current);
                if (el) {
                    el.focus();
                    pendingFocusRef.current = null;
                }
            });
        }
    }, [data]);

    const handleReorder2DGrid = (e) => {
        if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
            gridRoving.onKeyDown(e);
            return;
        }
        e.preventDefault();
        const activeEl = document.activeElement;
        const currentCard = activeEl.closest('.ch-column-dnd-card');
        const currentColumn = activeEl.closest('.ch-dnd-column');

        if (currentCard && currentColumn) {
            const isPreview = activeEl.classList.contains('preview-btn');

            if (e.key === 'ArrowDown') {
                if (!isPreview) {
                    const previewBtn = currentCard.querySelector('.preview-btn');
                    if (previewBtn) previewBtn.focus();
                } else {
                    const targetCard = currentCard.nextElementSibling;
                    if (targetCard) {
                        const dragBtn = targetCard.querySelector('.drag-handle-btn');
                        if (dragBtn) dragBtn.focus();
                    }
                }
            } else if (e.key === 'ArrowUp') {
                if (isPreview) {
                    const dragBtn = currentCard.querySelector('.drag-handle-btn');
                    if (dragBtn) dragBtn.focus();
                } else {
                    const targetCard = currentCard.previousElementSibling;
                    if (targetCard) {
                        const prevBtn = targetCard.querySelector('.preview-btn');
                        if (prevBtn) prevBtn.focus();
                    } else {
                        const swapBtn = currentColumn.querySelector('.swap-page-btn');
                        if (swapBtn) swapBtn.focus();
                    }
                }
            }
            else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                const targetCol = e.key === 'ArrowRight'
                    ? currentColumn.nextElementSibling
                    : currentColumn.previousElementSibling;

                if (targetCol && targetCol.classList.contains('ch-dnd-column')) {
                    const cardIndex = Array.from(currentColumn.querySelectorAll('.ch-column-dnd-card')).indexOf(currentCard);
                    const targetCards = targetCol.querySelectorAll('.ch-column-dnd-card');

                    if (targetCards.length > 0) {
                        const targetCard = targetCards[Math.min(cardIndex, targetCards.length - 1)];
                        const targetSelector = isPreview ? '.preview-btn' : '.drag-handle-btn';
                        const targetBtn = targetCard.querySelector(targetSelector);
                        if (targetBtn) targetBtn.focus();
                    } else {
                        const headerBtn = targetCol.querySelector('.swap-page-btn');
                        if (headerBtn) headerBtn.focus();
                    }
                }
            }
            return;
        }

        const currentHeader = activeEl.closest('.ch-dnd-column-title');
        if (currentHeader && currentColumn) {
            if (e.key === 'ArrowDown') {
                const firstCard = currentColumn.querySelector('.ch-column-dnd-card');
                if (firstCard) {
                    const dragBtn = firstCard.querySelector('.drag-handle-btn');
                    if (dragBtn) dragBtn.focus();
                }
            }
            else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                const allSwapBtns = Array.from(document.querySelectorAll('.swap-page-btn'));
                const currentIndex = allSwapBtns.indexOf(activeEl);
                if (currentIndex !== -1) {
                    const nextIndex = e.key === 'ArrowRight' ? currentIndex + 1 : currentIndex - 1;
                    if (nextIndex >= 0 && nextIndex < allSwapBtns.length) {
                        allSwapBtns[nextIndex].focus();
                    }
                }
            }
        }
    };

    const sectionOffset = useMemo(() => {
        if (!useSections || !sections || !sectionVal) return 0;
        let offset = 0;
        const sectionKeys = Object.keys(sections);

        for (const key of sectionKeys) {
            if (key === sectionVal) break;

            if (sections[key] && sections[key].questions) {
                offset += Object.keys(sections[key].questions).length;
            }
        }
        return offset;
    }, [useSections, sections, sectionVal]);

    const liveIndexMap = useMemo(() => {
        const map = {};
        let counter = sectionOffset;

        data.columnOrder?.forEach(colId => {
            data.columns[colId].questionIds.forEach(qId => {
                map[qId] = counter++;
            });
        });
        return map;
    }, [data.columnOrder, data.columns, sectionOffset]);

    return (
        <>
            {isLoading && <div className={'flexCentre ch-width-100 padding--l'}><Spin size="large" /></div>}
            {!isLoading && <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
                onDragCancel={handleDragCancel}
            >
                {/* Semantic: <section> to wrap the reorder control region */}
                <section aria-label="Reorder Questions" className={'flexCol'} style={{ padding: '12px', backgroundColor: '#eee' }}>
                    {useSections && <nav aria-label="Section Selector">
                        <Radio.Group
                            className={'padding-t--m padding-b--m'}
                            optionType="button"
                            defaultValue={objectHasEntry(initialData) ? Object.keys(initialData.sections)[0]: null}
                            onChange={onSectionChange}
                            options={Object.keys(initialData.sections).map(key => ({
                                label: 'Section '+key,
                                value: key
                            }))}
                        />
                    </nav>}
                    <div
                        className={'flexRow smallGap flexWrap'}
                        style={{overflowX: 'auto'}}
                        ref={gridRoving.ref}
                        onFocus={gridRoving.onFocus}
                        onClick={gridRoving.onClick}
                        onKeyDown={(e) => {
                            const activeEl = document.activeElement;
                            if (activeId !== null || activeEl?.getAttribute('aria-pressed') === 'true') {
                                return;
                            }
                            if (activeEl?.classList.contains('ch-reorder-btn') || activeEl?.classList.contains('swap-page-btn')) {
                                handleReorder2DGrid(e);
                            }
                        }}
                    >
                        {data.columnOrder?.map((columnId, index) => {
                            const column = data.columns[columnId];
                            const questions = column.questionIds.map(id => ({
                                ...data.questions[id],
                                liveIndex: liveIndexMap[id]
                            }));
                            return (
                                <Column
                                    id={columnId}
                                    key={columnId}
                                    column={column}
                                    index={index}
                                    questions={questions}
                                    columnTotal={data.columnOrder.length}
                                    handleMoveColumn={handleMoveColumn}
                                    test={test}
                                    handlePreview={handlePreview}
                                    setPendingFocus={(targetId) => { pendingFocusRef.current = targetId; }}
                                />
                            );
                        })}
                    </div>
                </section>
                <DragOverlay dropAnimation={null}>
                    {activeItem ? (
                        <div
                            className={`ch-column-dnd-card ${activeItem.originalSeq !== undefined && activeItem.originalSeq !== liveIndexMap[activeId] ? 'unsaved-order' : ''}`}
                            style={{ cursor: 'grabbing', opacity: 0.9, boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)' }}
                        >
                            <ReorderTestQuestionCard
                                initialIndex={activeItem.questionSeq}
                                index={liveIndexMap[activeId]}
                                originalSeq={activeItem.originalSeq}
                                key={activeItem.questionId}
                                test={test}
                                handlePreview={handlePreview}
                                isDragging={true}
                                {...activeItem}
                            />
                        </div>
                    ) : null}
                </DragOverlay>
            </DndContext>}
            <GenericDialog open={isQuestionPreviewOpen} title="Question Preview"
                           onClose={() => {
                               setQuestionPreviewOpen(false);
                           }}
                           buttons={[{name: 'Close'}]}>
                {objectHasEntry(previewQuestionData) &&
                    <PreviewTestQuestionCard {...previewQuestionData} />}
            </GenericDialog>
        </>
    );
});

const PreviewTestQuestionCard = ({formattedInstructions, maxMark, testDiffLevel, nonCalculator, answerTime, questionAnswerTypeGroupDisplay, questionType, test, concept, ...rest})=>{
    return(
    <article className={'padding--s'}>
        {formattedInstructions ? (<MathJaxHTMLContent html={formattedInstructions} disableInputs={true} />)
            :(questionType !== 'GROUP_QUESTION' ? 'Formatted instructions missing' : null)}
        {questionType === 'GROUP_QUESTION' && <GroupInstructions questionData={{...rest}} />}
        <footer className={'ch-test-editor-card-tags margin-t--l mediumGap flexColumn'}>
            <div className={'flexRow'}>
                {concept && concept.conceptName && <Tooltip title={<MathJaxHTMLContent html={concept.conceptName}/>}><Tag style={{width:'120px',overflow:'hidden', textOverflow:'ellipsis'}} color={'green'}><MathJaxHTMLContent html={concept.conceptName}/></Tag></Tooltip>}
                {rest.skillName && <Tooltip title={rest.skillName}><Tag style={{width:'120px',overflow:'hidden', textOverflow:'ellipsis'}} color={'gold'}>{rest.skillName}</Tag></Tooltip>}
            </div>
            <div className={'flexRow'}>
                <Tag color={'blue'}>{questionAnswerTypeGroupDisplay}</Tag>
                <Tag color={'purple'}>{maxMark} Mark</Tag>
                {answerTime && <Tag color={'gold'}>{formatQuestionAnswerTime(answerTime)}</Tag>}
                <Tag color={'volcano'}>Level {testDiffLevel}</Tag>
                {test.diagnosticTest && <Tag color={'magenta'}>{rest.skillContentDescriptor}</Tag>}
                {((nonCalculator !== null && !nonCalculator) && (test.exam || test.diagnosticTest)) &&
                    <span className="calculator-icon bi-calculator-fill" title="calculator question"
                          rel="tooltip"
                          data-tooltip="calculator question" data-tooltipdelay="true"
                          data-tooltipstatic="true"></span>}
                {test.exam && <span className={'ch-sml-font'}>[{rest.examCitations}]</span>}
            </div>
        </footer>
    </article>
)
}

export default ReorderView;